import {useEffect, useState} from 'react';
import {ALL_PLATFORMS, AppPlatform, AppVersions} from '../types/feedback';
import {
    getAppVersions,
    getStoreVersions,
    updateAppVersions,
    updatePlatformVersion
} from '../services/appVersionsService';

interface UseAppVersionsReturn {
    versions: AppVersions | null;
    storeVersions: Record<string, string>;
    loading: boolean;
    error: string | null;
    updateVersion: (platform: AppPlatform, version: string) => Promise<void>;
    refresh: () => Promise<void>;
}

export function useAppVersions(): UseAppVersionsReturn {
    const [versions, setVersions] = useState<AppVersions | null>(null);
    const [storeVersions, setStoreVersions] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchVersions = async () => {
        try {
            setLoading(true);
            setError(null);
            const [data, store] = await Promise.all([
                getAppVersions(),
                getStoreVersions().catch((): Record<string, string> => ({})),
            ]);
            setVersions(data);
            setStoreVersions(store);

            // Write changed store versions to Firestore
            const changed: Partial<Record<AppPlatform, string>> = {};
            for (const platform of ALL_PLATFORMS) {
                const storeVal = store[platform];
                if (storeVal && storeVal !== data?.[platform]) {
                    changed[platform] = storeVal;
                }
            }
            if (Object.keys(changed).length > 0) {
                await updateAppVersions(changed);
                setVersions((prev) => ({...prev, ...changed}));
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load app versions');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVersions();
    }, []);

    const updateVersion = async (platform: AppPlatform, version: string) => {
        await updatePlatformVersion(platform, version);
        // Update local state
        setVersions((prev) => ({
            ...prev,
            [platform]: version,
        }));
    };

    return {
        versions,
        storeVersions,
        loading,
        error,
        updateVersion,
        refresh: fetchVersions,
    };
}
